import { Box, Button, CircularProgress, Divider, IconButton, TextField, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { AsyncPaginate } from "react-select-async-paginate";
import Fetch from "../services/Fetch";
import AddIcon from '@mui/icons-material/Add';
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useAddTeacher } from "../hooks/UseAddTeacher";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/style.css';
import { buildAddTeacherFormData } from "../helper/AddTeacherFormData";
import { useWaits } from "../hooks/UseWait";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useConstants } from "../hooks/UseConstants";
import { parsePaginatedResponse } from "../utils/parsePaginatedResponse";

function SectionCard({ icon, title, subtitle, children, theme }) {
    return (
        <Box
            sx={{
                p: 2.5,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: "#eff6ff",
                        color: "#0D358C",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0D358C" }}>
                        {title}
                    </Typography>
                    {subtitle ? (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            {children}
        </Box>
    );
}

function AddTeacher({ onClickClose, setSnackBar, setTeachers }) {
    const { host, language } = useConstants();
    const { sendWait, setSendWait } = useWaits();
    const theme = useTheme();
    const intl = useIntl();
    const isAr = language === "ar";
    const { firstName, setFirstName, lastName, setLastName, specialization, setSpecialization, academicDegree,
        setAcademicDegree, experienceYears, setExperienceYears, birthdate, setBirthDate, email, setEmail, phoneNumber,
        setPhoneNumber, code, setCode, password, setPassword, confirmPassword, setConfirmPassword, major, setMajor } = useAddTeacher();
    const [academicDegreeValue, setAcademicDegreeValue] = useState(null);
    const [teacherSpecializationsValue, setTeacherSpecializationsValue] = useState(null);
    const [majorValue, setMajorValue] = useState(null);
    const selectStyles = useMemo(() => ({
        control: (base) => ({
            ...base,
            minHeight: 40,
            borderRadius: 8,
            borderColor: "#cbd5e1",
        }),
        option: (provided) => ({
            ...provided,
            color: "black",
        }),
    }), []);

    const handleChange = (value, country) => {
        const numberWithoutCode = value.replace(country.dialCode, "");
        setCode(country.dialCode);
        setPhoneNumber(numberWithoutCode);
    };

    const loadAcademicDegrees = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(`${host}/academic-degrees?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const { items, pagination } = parsePaginatedResponse(response);
        return {
            options: items.map((item) => ({
                value: item.id, label: language === 'en' ? item.name_en : item.name_ar,
            })),
            hasMore: pagination.current_page < pagination.last_page,
            additional: { page: page + 1 },
        };
    };

    const loadTeacherSpecializations = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(`${host}/teacher-specializations?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const { items, pagination } = parsePaginatedResponse(response);
        return {
            options: items.map((item) => ({
                value: item.id, label: language === 'en' ? item.name_en : item.name_ar,
            })),
            hasMore: pagination.current_page < pagination.last_page,
            additional: { page: page + 1 },
        };
    };

    const loadMajors = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(`${host}/majors?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const { items, pagination } = parsePaginatedResponse(response);
        return {
            options: items.map((item) => ({ value: item.id, label: language === 'en' ? item.name_en : item.name_ar })),
            hasMore: pagination.current_page < pagination.last_page,
            additional: { page: page + 1 },
        };
    };

    const resetInputs = () => {
        setFirstName('');
        setLastName('');
        setSpecialization('');
        setAcademicDegree('');
        setExperienceYears('');
        setBirthDate('2025-01-01');
        setEmail('');
        setPhoneNumber('');
        setCode('');
        setPassword('');
        setConfirmPassword('');
        setMajor('');
        setAcademicDegreeValue(null);
        setTeacherSpecializationsValue(null);
        setMajorValue(null);
    };

    const handleClose = () => {
        resetInputs();
        onClickClose();
    };

    const addTeacher = async () => {
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            setSnackBar('error', isAr ? 'أكمل الاسم والبريد الإلكتروني أولاً' : 'Complete the name and email first');
            return;
        }
        if (!specialization || !academicDegree) {
            setSnackBar('error', isAr ? 'اختر التخصص والدرجة الأكاديمية' : 'Choose specialization and academic degree');
            return;
        }
        if (!password || password !== confirmPassword) {
            setSnackBar('error', isAr ? 'تأكد من مطابقة كلمة المرور وتأكيدها' : 'Make sure password and confirmation match');
            return;
        }

        setSendWait(true);

        const formData = buildAddTeacherFormData({
            firstName: firstName,
            lastName: lastName,
            email: email,
            birthDate: birthdate,
            code: code,
            phoneNumber: phoneNumber,
            specialization: specialization,
            academicDegree: academicDegree,
            experienceYears: experienceYears,
            major: major,
            password: password,
            confirmPassword: confirmPassword
        });

        const result = await Fetch(host + `/admin/users/store`, 'POST', formData);

        if (result.status === 200) {
            setTeachers((prevTeachers) => [result.data.data.user, ...prevTeachers]);
            resetInputs();
            setSnackBar('success', isAr ? 'تمت إضافة المعلم بنجاح' : 'Teacher added successfully');
            onClickClose();
        } else if (result.status === 422) {
            setSnackBar('error', result.data.errors?.[0] || result.data.message);
        } else {
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }

        setSendWait(false);
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "94%", sm: "88%", md: "72%" },
                maxWidth: 980,
                maxHeight: "92vh",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(13,53,140,0.22)",
                display: "flex",
                flexDirection: "column",
            }}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <PersonAddAlt1OutlinedIcon sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}><FormattedMessage id='add_teacher' /></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "أضف معلماً جديداً مع بياناته التعليمية ووسائل التواصل." : "Add a new teacher with academic profile and contact details."}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={handleClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: "auto", flex: 1 }} className="none-view-scroll">
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <SectionCard
                        icon={<BadgeOutlinedIcon />}
                        title={isAr ? "البيانات الأساسية" : "Basic information"}
                        subtitle={isAr ? "اسم المعلم وبيانات الميلاد والخبرة" : "Teacher identity, birth date, and experience"}
                        theme={theme}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField value={firstName} onChange={(e) => setFirstName(e.target.value)} size="small" fullWidth label={<FormattedMessage id='first_name' />} />
                            <TextField value={lastName} onChange={(e) => setLastName(e.target.value)} size="small" fullWidth label={<FormattedMessage id='last_name' />} />
                            <TextField type="number" value={experienceYears} onChange={(e) => setExperienceYears(e.target.value)} size="small" fullWidth label={<FormattedMessage id='experience_years' />} />
                            <TextField value={birthdate} onChange={(e) => setBirthDate(e.target.value)} size="small" type="date" fullWidth label={<FormattedMessage id='birth_date' />} InputLabelProps={{ shrink: true }} />
                        </Box>
                    </SectionCard>

                    <SectionCard
                        icon={<SchoolOutlinedIcon />}
                        title={isAr ? "البيانات الأكاديمية" : "Academic data"}
                        subtitle={isAr ? "اختر التخصص العلمي، التخصص التعليمي، والدرجة الأكاديمية" : "Choose specialization, major, and academic degree"}
                        theme={theme}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.75, display: "block" }}>
                                    <FormattedMessage id='specialization' />
                                </Typography>
                                <AsyncPaginate
                                    value={teacherSpecializationsValue}
                                    loadOptions={loadTeacherSpecializations}
                                    onChange={(option) => {
                                        setTeacherSpecializationsValue(option || null);
                                        setSpecialization(option?.value || '');
                                    }}
                                    additional={{ page: 1 }}
                                    styles={selectStyles}
                                    isSearchable
                                    placeholder={intl.formatMessage({ id: 'specialization' })}
                                />
                            </Box>

                            <Box>
                                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.75, display: "block" }}>
                                    <FormattedMessage id='education_specialization' />
                                </Typography>
                                <AsyncPaginate
                                    value={majorValue}
                                    loadOptions={loadMajors}
                                    onChange={(option) => {
                                        setMajorValue(option || null);
                                        setMajor(option?.value || '');
                                    }}
                                    additional={{ page: 1 }}
                                    styles={selectStyles}
                                    isSearchable
                                    placeholder={intl.formatMessage({ id: 'education_specialization' })}
                                />
                            </Box>

                            <Box>
                                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.75, display: "block" }}>
                                    <FormattedMessage id='academic_degree' />
                                </Typography>
                                <AsyncPaginate
                                    value={academicDegreeValue}
                                    loadOptions={loadAcademicDegrees}
                                    onChange={(option) => {
                                        setAcademicDegreeValue(option || null);
                                        setAcademicDegree(option?.value || '');
                                    }}
                                    additional={{ page: 1 }}
                                    styles={selectStyles}
                                    isSearchable
                                    placeholder={intl.formatMessage({ id: 'academic_degree' })}
                                />
                            </Box>
                        </Box>
                    </SectionCard>

                    <SectionCard
                        icon={<BadgeOutlinedIcon />}
                        title={isAr ? "بيانات التواصل" : "Contact details"}
                        subtitle={isAr ? "البريد الإلكتروني ورقم الهاتف" : "Email and phone number"}
                        theme={theme}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField value={email} onChange={(e) => setEmail(e.target.value)} size="small" fullWidth label={<FormattedMessage id='email' />} />
                            <Box dir="ltr" sx={{ "& .form-control": { width: "100% !important", height: "40px !important", background: "transparent !important", color: `${theme.palette.text.primary} !important`, borderColor: `${theme.palette.mode === "dark" ? theme.palette.divider : "#cbd5e1"} !important`, borderRadius: "8px !important" }, "& .flag-dropdown": { background: theme.palette.mode === 'dark' ? theme.palette.background.default : "#fff" } }}>
                                <PhoneInput
                                    country={'us'}
                                    containerStyle={{ width: "100%" }}
                                    buttonStyle={{ background: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff', borderColor: theme.palette.mode === "dark" ? theme.palette.divider : "#cbd5e1" }}
                                    inputStyle={{ width: '100%', height: 40, color: theme.palette.text.primary, background: 'transparent' }}
                                    onChange={handleChange}
                                    value={`${code || ""}${phoneNumber || ""}`}
                                />
                            </Box>
                        </Box>
                    </SectionCard>

                    <SectionCard
                        icon={<LockOutlinedIcon />}
                        title={isAr ? "إعدادات الدخول" : "Access settings"}
                        subtitle={isAr ? "كلمة المرور وتأكيدها للحساب الجديد" : "Password and confirmation for the new account"}
                        theme={theme}
                    >
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField value={password} onChange={(e) => setPassword(e.target.value)} size="small" type="password" fullWidth label={<FormattedMessage id='password' />} />
                            <TextField value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} size="small" type="password" fullWidth label={<FormattedMessage id='confirm_password' />} />
                        </Box>
                    </SectionCard>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                    <Button onClick={handleClose} variant="outlined" disabled={sendWait} sx={{ minWidth: 140 }}>
                        <FormattedMessage id='cancel' />
                    </Button>
                    <Button
                        variant="contained"
                        disabled={sendWait}
                        onClick={addTeacher}
                        startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                        sx={{ minWidth: 170, bgcolor: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#1d4ed8" } }}
                    >
                        {sendWait ? (isAr ? "جاري الإضافة..." : "Adding...") : <FormattedMessage id='add' />}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default AddTeacher;